import React, { useState } from 'react';
import { Plus, X, SpinnerGap } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FieldType } from 'shared/types/asset';

export interface NewFieldData {
  fieldName: string;
  displayName: string;
  fieldType: FieldType;
  isRequired: boolean;
  enumValues?: string[];
}

interface InlineFieldCreatorProps {
  onCreateField: (field: NewFieldData) => Promise<void>;
  isCreating: boolean;
  existingFieldNames?: string[];
  onCancel?: () => void;
}

const fieldTypes = [
  { value: FieldType.STRING, label: 'Tekst' },
  { value: FieldType.NUMBER, label: 'Getal' },
  { value: FieldType.BOOLEAN, label: 'Ja/Nee' },
  { value: FieldType.DATE, label: 'Datum' },
  { value: FieldType.ENUM, label: 'Dropdown' },
  { value: FieldType.MULTI_ENUM, label: 'Meerkeuze' },
];

export const InlineFieldCreator: React.FC<InlineFieldCreatorProps> = ({
  onCreateField,
  isCreating,
  existingFieldNames = [],
  onCancel,
}) => {
  const [displayName, setDisplayName] = useState('');
  const [fieldType, setFieldType] = useState<FieldType>(FieldType.STRING);
  const [isRequired, setIsRequired] = useState(false);
  const [enumValues, setEnumValues] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Auto-generate fieldName from displayName
  const generateFieldName = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  };

  const handleAddEnumValue = () => {
    setEnumValues([...enumValues, '']);
  };

  const handleUpdateEnumValue = (index: number, value: string) => {
    const newValues = [...enumValues];
    newValues[index] = value;
    setEnumValues(newValues);
  };

  const handleRemoveEnumValue = (index: number) => {
    setEnumValues(enumValues.filter((_, i) => i !== index));
  };

  const handleFieldTypeChange = (value: FieldType) => {
    setFieldType(value);
    // Clear enum values if switching away from enum type
    if (value !== FieldType.ENUM && value !== FieldType.MULTI_ENUM) {
      setEnumValues([]);
    }
  };

  const validate = (): boolean => {
    setError(null);

    if (!displayName.trim()) {
      setError('Voer een veldnaam in');
      return false;
    }

    const fieldName = generateFieldName(displayName);
    if (existingFieldNames.includes(fieldName)) {
      setError('Er bestaat al een veld met deze naam');
      return false;
    }

    if ((fieldType === FieldType.ENUM || fieldType === FieldType.MULTI_ENUM) && enumValues.filter(v => v.trim()).length === 0) {
      setError('Voeg minimaal één optie toe voor dit veldtype');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const fieldName = generateFieldName(displayName);
    const fieldData: NewFieldData = {
      fieldName,
      displayName: displayName.trim(),
      fieldType,
      isRequired,
      enumValues: (fieldType === FieldType.ENUM || fieldType === FieldType.MULTI_ENUM)
        ? enumValues.filter(v => v.trim())
        : undefined,
    };

    await onCreateField(fieldData);

    // Reset form on success
    setDisplayName('');
    setFieldType(FieldType.STRING);
    setIsRequired(false);
    setEnumValues([]);
    setError(null);
  };

  const handleCancel = () => {
    setDisplayName('');
    setFieldType(FieldType.STRING);
    setIsRequired(false);
    setEnumValues([]);
    setError(null);
    onCancel?.();
  };

  const showEnumValues = fieldType === FieldType.ENUM || fieldType === FieldType.MULTI_ENUM;

  return (
    <div className="p-4 border rounded-lg bg-muted/20 space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Nieuw veld toevoegen</Label>
        {onCancel && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={handleCancel}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Display Name */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Weergavenaam</Label>
          <Input
            placeholder="Bijv. Merk, Serienummer..."
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="h-9"
            disabled={isCreating}
          />
        </div>

        {/* Field Type */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Type</Label>
          <Select
            value={fieldType}
            onValueChange={(value) => handleFieldTypeChange(value as FieldType)}
            disabled={isCreating}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fieldTypes.map((ft) => (
                <SelectItem key={ft.value} value={ft.value}>
                  {ft.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Required Toggle */}
      <div className="flex items-center gap-2">
        <Switch
          checked={isRequired}
          onCheckedChange={setIsRequired}
          disabled={isCreating}
        />
        <Label className="text-sm">Verplicht veld</Label>
      </div>

      {/* Enum Values for Dropdown/Multi-select fields */}
      {showEnumValues && (
        <div className="pl-3 border-l-2 border-muted space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Opties</Label>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={handleAddEnumValue}
              disabled={isCreating}
            >
              <Plus className="h-3 w-3 mr-1" />
              Optie
            </Button>
          </div>
          {enumValues.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">
              Voeg opties toe voor dit veld
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {enumValues.map((enumValue, index) => (
                <div
                  key={index}
                  className="flex items-center gap-1 bg-background rounded-md px-2 py-1 border"
                >
                  <Input
                    value={enumValue}
                    onChange={(e) => handleUpdateEnumValue(index, e.target.value)}
                    placeholder="Waarde"
                    className="h-5 w-24 text-xs border-0 bg-transparent p-0 focus-visible:ring-0"
                    disabled={isCreating}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4"
                    onClick={() => handleRemoveEnumValue(index)}
                    disabled={isCreating}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-2 pt-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleCancel}
          disabled={isCreating}
        >
          Annuleren
        </Button>
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={isCreating || !displayName.trim()}
        >
          {isCreating ? (
            <>
              <SpinnerGap className="h-4 w-4 mr-2 animate-spin" />
              Toevoegen...
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-2" />
              Veld toevoegen
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default InlineFieldCreator;
